import React from "react";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@material-ui/core";
import { css } from "@emotion/css";
import SearchField from "./SearchField";
import {
  useQuestions,
  useQuestionnaires,
  useQuestionGroups,
  useCreateQuestion,
  useCreateQuestionnaire,
  useCreateQuestionGroup,
} from "../queries";

import type { EntityType, Selection, PickType, PickMode } from "../uiTypes";
import { RESPONSE_TYPE_ENTRIES } from "../responseTypes";

type SidebarProps = {
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onCreated: (selection: Selection) => void;
  tab: EntityType;
  pickMode: PickMode;
  onPick: (id: number) => void;
  onCancelPick: () => void;
};

const RESPONSE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All types" },
  ...RESPONSE_TYPE_ENTRIES.map(([value, label]) => ({ value, label })),
];

const USAGE_OPTIONS = [
  { value: "", label: "All" },
  { value: "used", label: "Used" },
  { value: "unused", label: "Unused" },
];

const pickTypeToTab: Record<PickType, EntityType> = {
  question: "questions",
  questionGroup: "questionGroups",
};

const Sidebar: React.VFC<SidebarProps> = ({ selection, onSelect, onCreated, tab, pickMode, onPick, onCancelPick }) => {
  const effectiveTab = pickMode ? pickTypeToTab[pickMode.pickType] : tab;
  const [questionSearchTerm, setQuestionSearchTerm] = React.useState("");
  const [questionCommittedSearch, setQuestionCommittedSearch] = React.useState("");
  const [questionnaireSearchTerm, setQuestionnaireSearchTerm] = React.useState("");
  const [questionnaireCommittedSearch, setQuestionnaireCommittedSearch] = React.useState("");
  const [groupSearchTerm, setGroupSearchTerm] = React.useState("");
  const [groupCommittedSearch, setGroupCommittedSearch] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [usageFilter, setUsageFilter] = React.useState("");

  const commitQuestionSearch = (override?: string) => setQuestionCommittedSearch(override ?? questionSearchTerm);
  const commitQuestionnaireSearch = (override?: string) => setQuestionnaireCommittedSearch(override ?? questionnaireSearchTerm);
  const commitGroupSearch = (override?: string) => setGroupCommittedSearch(override ?? groupSearchTerm);

  const hasQuestionFilters = !!(questionSearchTerm || questionCommittedSearch || typeFilter || usageFilter);
  const hasQuestionnaireFilters = !!(questionnaireSearchTerm || questionnaireCommittedSearch);
  const hasGroupFilters = !!(groupSearchTerm || groupCommittedSearch);

  const clearQuestionFilters = () => {
    setQuestionSearchTerm("");
    setQuestionCommittedSearch("");
    setTypeFilter("");
    setUsageFilter("");
  };
  const clearQuestionnaireFilters = () => {
    setQuestionnaireSearchTerm("");
    setQuestionnaireCommittedSearch("");
  };
  const clearGroupFilters = () => {
    setGroupSearchTerm("");
    setGroupCommittedSearch("");
  };

  const questionFilters = React.useMemo(
    () => ({
      ...(questionCommittedSearch && { search: questionCommittedSearch }),
      ...(typeFilter && { type: typeFilter }),
      ...(usageFilter && { usage: usageFilter }),
    }),
    [questionCommittedSearch, typeFilter, usageFilter]
  );

  const questionnaireFilters = React.useMemo(
    () => (questionnaireCommittedSearch ? { search: questionnaireCommittedSearch } : undefined),
    [questionnaireCommittedSearch]
  );

  const groupFilters = React.useMemo(
    () => (groupCommittedSearch ? { search: groupCommittedSearch } : undefined),
    [groupCommittedSearch]
  );

  const questionsQuery = useQuestions(questionFilters);
  const questionnairesQuery = useQuestionnaires(questionnaireFilters);
  const questionGroupsQuery = useQuestionGroups(groupFilters);

  const createQuestion = useCreateQuestion();
  const createQuestionnaire = useCreateQuestionnaire();
  const createQuestionGroup = useCreateQuestionGroup();

  const handleCreateQuestion = () => {
    createQuestion.mutate(undefined, {
      onSuccess: (data) => {
        onCreated({ type: "question", id: data.id });
      },
    });
  };

  const handleCreateQuestionnaire = () => {
    createQuestionnaire.mutate(undefined, {
      onSuccess: (data) => {
        onCreated({ type: "questionnaire", id: data.id });
      },
    });
  };

  const handleCreateQuestionGroup = () => {
    createQuestionGroup.mutate(undefined, {
      onSuccess: (data) => {
        onCreated({ type: "questionGroup", id: data.id });
      },
    });
  };

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const renderQuestionsList = () => {
    if (questionsQuery.isLoading) return <CircularProgress size={24} />;
    if (questionsQuery.error) return <Typography color="error">Failed to load questions</Typography>;

    return (
      <>
        <Box className={styles.filters}>
          {!pickMode && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={handleCreateQuestion}
              disabled={createQuestion.isLoading}
            >
              {createQuestion.isLoading ? "Creating..." : "New Question"}
            </Button>
          )}

          <SearchField value={questionSearchTerm} onChange={setQuestionSearchTerm} onCommit={commitQuestionSearch} placeholder="Search questions..." />

          <FormControl variant="outlined" size="small" fullWidth className={styles.filterField}>
            <InputLabel>Filter by type</InputLabel>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as string)} label="Filter by type">
              {RESPONSE_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" fullWidth className={styles.filterField}>
            <InputLabel>Filter by usage</InputLabel>
            <Select value={usageFilter} onChange={(e) => setUsageFilter(e.target.value as string)} label="Filter by usage">
              {USAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box className={styles.countRow}>
            <Button
              size="small"
              className={styles.clearAllButton}
              onClick={clearQuestionFilters}
              disabled={!hasQuestionFilters}
            >
              Clear all
            </Button>
            <Typography variant="caption" color="textSecondary">
              {questionsQuery.data?.length ?? 0} match{(questionsQuery.data?.length ?? 0) !== 1 ? "es" : ""}
            </Typography>
          </Box>
        </Box>
        <List dense className={styles.list}>
          {questionsQuery.data?.map((q) => (
            <ListItem
              key={q.id}
              button
              selected={!pickMode && selection?.type === "question" && selection.id === q.id}
              onClick={() => pickMode ? onPick(q.id) : onSelect({ type: "question", id: q.id })}
            >
              <ListItemText
                primary={truncate(q.question || `Question #${q.id}`, 40)}
                secondary={q.semantic_key || null}
                secondaryTypographyProps={{ className: styles.semanticKey }}
              />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderQuestionnairesList = () => {
    if (questionnairesQuery.isLoading) return <CircularProgress size={24} />;
    if (questionnairesQuery.error) return <Typography color="error">Failed to load questionnaires</Typography>;

    return (
      <>
        <Box className={styles.filters}>
          {!pickMode && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={handleCreateQuestionnaire}
              disabled={createQuestionnaire.isLoading}
            >
              {createQuestionnaire.isLoading ? "Creating..." : "New Questionnaire"}
            </Button>
          )}
          <SearchField value={questionnaireSearchTerm} onChange={setQuestionnaireSearchTerm} onCommit={commitQuestionnaireSearch} placeholder="Search questionnaires..." />
          <Box className={styles.countRow}>
            <Button
              size="small"
              className={styles.clearAllButton}
              onClick={clearQuestionnaireFilters}
              disabled={!hasQuestionnaireFilters}
            >
              Clear all
            </Button>
            <Typography variant="caption" color="textSecondary">
              {questionnairesQuery.data?.length ?? 0} match{(questionnairesQuery.data?.length ?? 0) !== 1 ? "es" : ""}
            </Typography>
          </Box>
        </Box>
        <List dense className={styles.list}>
          {questionnairesQuery.data?.map((b) => (
            <ListItem
              key={b.id}
              button
              selected={!pickMode && selection?.type === "questionnaire" && selection.id === b.id}
              onClick={() => pickMode ? onPick(b.id) : onSelect({ type: "questionnaire", id: b.id })}
            >
              <ListItemText primary={b.name || `Questionnaire #${b.id}`} />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderQuestionGroupsList = () => {
    if (questionGroupsQuery.isLoading) return <CircularProgress size={24} />;
    if (questionGroupsQuery.error) return <Typography color="error">Failed to load question groups</Typography>;

    return (
      <>
        <Box className={styles.filters}>
          {!pickMode && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={handleCreateQuestionGroup}
              disabled={createQuestionGroup.isLoading}
            >
              {createQuestionGroup.isLoading ? "Creating..." : "New Question Group"}
            </Button>
          )}
          <SearchField value={groupSearchTerm} onChange={setGroupSearchTerm} onCommit={commitGroupSearch} placeholder="Search question groups..." />
          <Box className={styles.countRow}>
            <Button
              size="small"
              className={styles.clearAllButton}
              onClick={clearGroupFilters}
              disabled={!hasGroupFilters}
            >
              Clear all
            </Button>
            <Typography variant="caption" color="textSecondary">
              {questionGroupsQuery.data?.length ?? 0} match{(questionGroupsQuery.data?.length ?? 0) !== 1 ? "es" : ""}
            </Typography>
          </Box>
        </Box>
        <List dense className={styles.list}>
          {questionGroupsQuery.data?.map((qg) => (
            <ListItem
              key={qg.id}
              button
              selected={!pickMode && selection?.type === "questionGroup" && selection.id === qg.id}
              onClick={() => pickMode ? onPick(qg.id) : onSelect({ type: "questionGroup", id: qg.id })}
            >
              <ListItemText primary={qg.name || `Question Group #${qg.id}`} />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderList = () => {
    if (effectiveTab === "questions") return renderQuestionsList();
    if (effectiveTab === "questionnaires") return renderQuestionnairesList();
    if (effectiveTab === "questionGroups") return renderQuestionGroupsList();
    return null;
  };

  const pickBannerText = pickMode?.pickType === "question" ? "Pick a question" : "Pick a question group";

  return (
    <div className={styles.container}>
      {pickMode && (
        <Box className={styles.pickBanner}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>{pickBannerText}</Typography>
          <Button size="small" onClick={onCancelPick}>Cancel</Button>
        </Box>
      )}
      <Box className={styles.listContainer}>{renderList()}</Box>
    </div>
  );
};

export default Sidebar;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  listContainer: css`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  `,
  filters: css`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-bottom: 1px solid #e0e0e0;
  `,
  createButton: css`
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  `,
  filterField: css`
    & .MuiOutlinedInput-root {
      background: white;
    }
    & .MuiSelect-outlined.MuiSelect-outlined {
      padding: 6px 28px 6px 10px;
      font-size: 0.8125rem;
    }
    & .MuiInputLabel-outlined {
      transform: translate(10px, 8px) scale(1);
    }
    & .MuiInputLabel-outlined.MuiInputLabel-shrink {
      transform: translate(10px, -6px) scale(0.75);
    }
  `,
  countRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  `,
  clearAllButton: css`
    padding: 0;
    min-width: 0;
    & .MuiButton-label {
      text-transform: none;
      font-size: 0.75rem;
    }
  `,
  list: css`
    padding: 0;
    flex: 1;
    overflow-y: auto;
  `,
  semanticKey: css`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.7rem;
  `,
  pickBanner: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #e3f2fd;
    border-bottom: 1px solid #90caf9;
  `,
};
